import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { ScenarioEvent } from '../workflow.interface';
import { ScenarioExecutionLog } from '@tj/tj-dmp-lib-api';

@Component({
    selector: 'dmp-workflow-log',
    templateUrl: './workflow-log.component.html',
    styleUrls: ['./workflow-log.component.scss'],
    standalone: false
})
export class WorkflowLogComponent implements OnInit, OnChanges {

    @Input() logs: ScenarioExecutionLog[];
    @Input() version: number;
    @Input() totalElements: number = 0;
    @Input() serverSidePagination: boolean = false;
    @Output() pageChange = new EventEmitter<number>();

    // Pagination
    currentPage: number = 1;
    itemsPerPage: number = 10;
    paginatedLogs: ScenarioExecutionLog[] = [];
    totalPages: number = 0;

    // Email details modal
    showEmailModal: boolean = false;
    selectedEmailContext: any = null;

    constructor() { }

    ngOnInit(): void {
        this.updatePagination();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['logs'] || changes['totalElements']) {
            this.updatePagination();
        }
    }

    updatePagination(): void {
        if (this.serverSidePagination) {
            // Server-side pagination
            this.paginatedLogs = this.logs || [];
            this.totalPages = this.totalElements ? Math.ceil(this.totalElements / this.itemsPerPage) : 0;
        } else {
            // Client-side pagination
            if (!this.logs || this.logs.length === 0) {
                this.paginatedLogs = [];
                this.totalPages = 0;
                return;
            }

            this.totalPages = Math.ceil(this.logs.length / this.itemsPerPage);
            const startIndex = (this.currentPage - 1) * this.itemsPerPage;
            const endIndex = startIndex + this.itemsPerPage;
            this.paginatedLogs = this.logs.slice(startIndex, endIndex);
        }
    }

    goToPage(page: number): void {
        if (page >= 1 && page <= this.totalPages) {
            this.currentPage = page;
            if (this.serverSidePagination) {
                this.pageChange.emit(page);
            } else {
                this.updatePagination();
            }
        }
    }

    nextPage(): void {
        if (this.currentPage < this.totalPages) {
            this.currentPage++;
            if (this.serverSidePagination) {
                this.pageChange.emit(this.currentPage);
            } else {
                this.updatePagination();
            }
        }
    }

    previousPage(): void {
        if (this.currentPage > 1) {
            this.currentPage--;
            if (this.serverSidePagination) {
                this.pageChange.emit(this.currentPage);
            } else {
                this.updatePagination();
            }
        }
    }

    getPageNumbers(): number[] {
        const pages: number[] = [];
        const maxPagesToShow = 7; // Show up to 7 paginatio

        if (this.totalPages <= maxPagesToShow) {
            // Show all pages if total is less than or equal to max
            for (let i = 1; i <= this.totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Show pagination with ellipsis
            const leftSiblingIndex = Math.max(this.currentPage - 1, 1);
            const rightSiblingIndex = Math.min(this.currentPage + 1, this.totalPages);

            const shouldShowLeftDots = leftSiblingIndex > 2;
            const shouldShowRightDots = rightSiblingIndex < this.totalPages - 1;

            if (!shouldShowLeftDots && shouldShowRightDots) {
                // Show first 5 pages + dots + last page
                for (let i = 1; i <= 5; i++) {
                    pages.push(i);
                }
                pages.push(-1); // Ellipsis
                pages.push(this.totalPages);
            } else if (shouldShowLeftDots && !shouldShowRightDots) {
                // Show first + dots + last 5 pages
                pages.push(1);
                pages.push(-1); // Ellipsis
                for (let i = this.totalPages - 4; i <= this.totalPages; i++) {
                    pages.push(i);
                }
            } else if (shouldShowLeftDots && shouldShowRightDots) {
                // Show first + dots + middle 3 + dots + last
                pages.push(1);
                pages.push(-1); // Ellipsis
                for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
                    pages.push(i);
                }
                pages.push(-1); // Ellipsis
                pages.push(this.totalPages);
            } else {
                // Show all pages (ça devrait pas arriver, mais juste au cas ou, si ça arrive ça fonctionne)
                for (let i = 1; i <= this.totalPages; i++) {
                    pages.push(i);
                }
            }
        }

        return pages;
    }

    getStatusClass(status: ScenarioExecutionLog.StatusEnum): string {
        switch (status) {
            case ScenarioExecutionLog.StatusEnum.SUCCESS:
                return 'status-success';
            case ScenarioExecutionLog.StatusEnum.FAILED:
                return 'status-failed';
            case ScenarioExecutionLog.StatusEnum.STARTED:
                return 'status-started';
            case ScenarioExecutionLog.StatusEnum.PAUSED:
                return 'status-paused';
            case ScenarioExecutionLog.StatusEnum.STOPPED:
                return 'status-stopped';
            default:
                return 'status-started';
        }
    }

    getStatusLabel(status: ScenarioExecutionLog.StatusEnum): string {
        switch (status) {
            case ScenarioExecutionLog.StatusEnum.SUCCESS:
                return 'success';
            case ScenarioExecutionLog.StatusEnum.FAILED:
                return 'failed';
            case ScenarioExecutionLog.StatusEnum.STARTED:
                return 'started';
            case ScenarioExecutionLog.StatusEnum.PAUSED:
                return 'paused';
            case ScenarioExecutionLog.StatusEnum.STOPPED:
                return 'stopped';
            default:
                return 'started';
        }
    }

    isEmailEvent(log: ScenarioExecutionLog): boolean {
        return log.context && log.context._isEmailEvent === true;
    }

    showEmailDetails(log: ScenarioExecutionLog): void {
        this.selectedEmailContext = log.context;
        this.showEmailModal = true;
    }

    getEmailTotal(): number {
        return this.selectedEmailContext?.recipients?.length || 0;
    }

    getEmailSuccessful(): number {
        if (this.selectedEmailContext?.status === 'success') {
            return this.getEmailTotal();
        }
        return 0;
    }

    getEmailFailed(): number {
        if (this.selectedEmailContext?.status !== 'success') {
            return this.getEmailTotal();
        }
        return 0;
    }

}
